import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import ExportToolbar from "@/components/ExportToolbar";
import DateRangePicker from "@/components/DateRangePicker";
import { useReportRange, formatRangeLabel } from "@/lib/useReportRange";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { exportExcel, exportPDF } from "@/lib/exporters";
import { Pencil, ChevronRight, ExternalLink } from "lucide-react";

// Tally-style: row showing the current ledger on the Dr side narrates the
// contra (Cr) ledger(s) prefixed with "To"; on the Cr side, prefixed with "By".
const drPrefix = "To ";
const crPrefix = "By ";

// Format running balance with Dr/Cr suffix instead of negative numbers.
const fmtBalance = (n) => {
  const abs = Math.abs(n || 0);
  if (abs < 0.005) return "0.00";
  return `${formatINR(abs)} ${n >= 0 ? "Dr" : "Cr"}`;
};

function ParticularsCell({ row, onContraClick }) {
  const isOpening = row.type === "Opening Balance";
  if (isOpening) {
    return <span className="italic text-gray-500">Opening Balance</span>;
  }
  const contra = row.contra || [];
  const prefix = row.debit > 0 ? drPrefix : crPrefix;
  // No contra (rare — e.g. legacy single-leg row) → fall back to narration/party.
  if (!contra.length) {
    return <span className="text-gray-700">{row.particulars || row.narration || "—"}</span>;
  }
  return (
    <div className="space-y-0.5">
      <div className="flex flex-wrap gap-x-1 gap-y-0.5">
        <span className="text-gray-400 font-medium">{prefix.trim()}</span>
        {contra.map((c, idx) => (
          <button
            key={`${c.ledger_id}-${idx}`}
            type="button"
            onClick={(e) => { e.stopPropagation(); onContraClick && onContraClick(c); }}
            className="text-blue-700 hover:text-blue-900 hover:underline font-medium"
            title={`Filter to ${c.ledger_name}`}
            data-testid={`contra-ledger-${c.ledger_id || idx}`}
          >
            {c.ledger_name}{idx < contra.length - 1 ? "," : ""}
          </button>
        ))}
        {contra.length > 1 && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="ml-1 text-[10px] px-1 rounded bg-gray-100 text-gray-500 cursor-help">i</span>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                <div className="space-y-1">
                  {contra.map((c, idx) => (
                    <div key={`${c.ledger_id || "x"}-tip-${idx}`} className="flex justify-between gap-3 font-mono text-xs">
                      <span>{c.ledger_name}</span>
                      <span>{formatINR(c.amount)} {c.dr_cr}</span>
                    </div>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {row.narration && (
        <div className="text-[11px] text-gray-400 italic truncate" title={row.narration}>
          {row.narration}
        </div>
      )}
    </div>
  );
}

function Inner() {
  const { activeId, active } = useCompany();
  const { range, setRange, applied, apply, params } = useReportRange();
  const dirty = range.start !== applied.start || range.end !== applied.end;
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeId) return;
    api.get(`/companies/${activeId}/reports/all-ledgers`, { params })
      .then((r) => { setData(r.data); setLoading(false); });
  }, [activeId, applied.start, applied.end, params]);

  const filtered = useMemo(() => filter
    ? data.filter((l) =>
        l.name.toLowerCase().includes(filter.toLowerCase()) ||
        l.group.toLowerCase().includes(filter.toLowerCase())
      )
    : data, [data, filter]);

  const meta = { company: active?.name };

  const onContraClick = (c) => {
    // Quick-jump: set the filter to the contra ledger name so the user can
    // see that ledger's full statement (the next matching <section> renders).
    setFilter(c.ledger_name);
    // Scroll the matching section into view shortly after re-render.
    setTimeout(() => {
      const el = document.querySelector(`[data-ledger-id="${c.ledger_id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const onExcel = () => {
    const sheets = filtered.map((L) => ({
      name: L.name.slice(0, 28),
      rows: [
        [`Ledger: ${L.name}`],
        [`Group: ${L.group}`],
        [`Opening: ${formatINR(L.opening_balance)}`],
        [],
        ["Date", "Voucher", "Type", "Particulars (Contra)", "Narration", "Debit", "Credit", "Balance"],
        ...L.rows.map((r) => {
          const prefix = r.type === "Opening Balance" ? "" : (r.debit > 0 ? drPrefix : crPrefix);
          const part = r.type === "Opening Balance" ? "Opening Balance" : (r.contra_label ? `${prefix}${r.contra_label}` : (r.particulars || ""));
          return [r.date, r.voucher_no, r.type, part, r.narration || "", r.debit, r.credit, r.balance];
        }),
        [],
        ["", "", "", "Totals", "", L.total_debit, L.total_credit, L.closing_balance],
      ],
    }));
    if (sheets.length === 0) sheets.push({ name: "All Ledgers", rows: [["No data"]] });

    const index = {
      name: "Summary",
      rows: [
        ["Ledger", "Group", "Opening", "Total Debit", "Total Credit", "Closing"],
        ...filtered.map((L) => [L.name, L.group, L.opening_balance, L.total_debit, L.total_credit, L.closing_balance]),
      ],
    };
    exportExcel(`all-ledgers-${active?.name || "report"}`, [index, ...sheets]);
  };

  const onPDF = () => {
    const sections = [
      {
        title: "Summary",
        headers: ["Ledger", "Group", "Opening", "Debit", "Credit", "Closing"],
        rows: filtered.map((L) => [
          L.name, L.group, formatINR(L.opening_balance),
          formatINR(L.total_debit), formatINR(L.total_credit), formatINR(L.closing_balance),
        ]),
      },
    ];
    filtered.forEach((L) => {
      sections.push({
        title: `${L.name}  —  ${L.group}  (Closing: ${fmtBalance(L.closing_balance)})`,
        headers: ["Date", "Voucher", "Type", "Particulars", "Debit", "Credit", "Balance"],
        rows: L.rows.map((r) => {
          const prefix = r.type === "Opening Balance" ? "" : (r.debit > 0 ? drPrefix : crPrefix);
          const part = r.type === "Opening Balance" ? "Opening Balance" : (r.contra_label ? `${prefix}${r.contra_label}` : (r.particulars || ""));
          return [r.date, r.voucher_no, r.type, part, formatINR(r.debit), formatINR(r.credit), formatINR(r.balance)];
        }),
      });
    });
    exportPDF(`all-ledgers-${active?.name || "report"}`, "All Ledgers — Detailed Report", sections, meta);
  };

  return (
    <>
      <PageHeader
        title="All Ledgers — Detailed"
        subtitle={`${active?.name || ""}  •  ${formatRangeLabel(range)}`}
        actions={<ExportToolbar onExcel={onExcel} onPDF={onPDF} onPrint={() => window.print()} />}
      />
      <div className="p-6 space-y-4">
        <DateRangePicker start={range.start} end={range.end} onChange={setRange} onApply={apply} dirty={dirty} />
        <div className="flex gap-3 flex-wrap items-center">
          <Input
            placeholder="Filter by ledger name or group…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="max-w-sm"
            data-testid="input-filter-all-ledgers"
          />
          {filter && (
            <button
              onClick={() => setFilter("")}
              className="text-xs text-gray-500 hover:text-gray-900 underline"
              data-testid="btn-clear-filter"
            >
              Clear filter
            </button>
          )}
          <span className="ml-auto text-xs text-gray-500 font-mono">{filtered.length} ledger(s)</span>
        </div>

        {loading && <div className="text-sm text-gray-500">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="border border-dashed border-gray-300 rounded p-10 text-center text-sm text-gray-500">
            No ledgers found
          </div>
        )}

        <div className="space-y-6" data-testid="all-ledgers-report">
          {filtered.map((L) => (
            <section
              key={L.id}
              data-ledger-id={L.id}
              className="border border-gray-200 rounded bg-white overflow-hidden"
            >
              <header className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <h3 className="font-semibold text-gray-900">{L.name}</h3>
                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{L.group}</span>
                <Link
                  to={`/reports/ledger/${L.id}`}
                  className="text-xs text-blue-700 hover:text-blue-900 hover:underline inline-flex items-center gap-1"
                  data-testid={`btn-view-statement-${L.id}`}
                >
                  View Statement <ExternalLink className="w-3 h-3" />
                </Link>
                <span className="text-xs text-gray-500 ml-auto font-mono">
                  Opening: <span className="text-gray-900">{fmtBalance(L.opening_balance)}</span>
                </span>
                <span className="text-xs text-gray-500 font-mono">
                  Closing: <span className={`font-semibold ${L.closing_balance >= 0 ? "text-blue-700" : "text-emerald-700"}`}>
                    {fmtBalance(L.closing_balance)}
                  </span>
                </span>
              </header>
              <table className="tx-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Voucher</th><th>Type</th><th>Particulars</th>
                    <th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Balance</th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {L.rows.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-6 text-sm text-gray-400">No transactions</td></tr>
                  )}
                  {L.rows.map((r, i) => (
                    <tr key={`${r.voucher_id || "open"}-${r.date}-${i}`} data-testid={`ledger-row-${L.id}-${i}`}>
                      <td className="font-mono text-xs whitespace-nowrap">{r.date}</td>
                      <td className="font-mono whitespace-nowrap">{r.voucher_no}</td>
                      <td>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 whitespace-nowrap">{r.type}</span>
                      </td>
                      <td className="max-w-md">
                        <ParticularsCell row={r} onContraClick={onContraClick} />
                      </td>
                      <td className="tx-num">{r.debit ? formatINR(r.debit) : ""}</td>
                      <td className="tx-num">{r.credit ? formatINR(r.credit) : ""}</td>
                      <td className="tx-num">{fmtBalance(r.balance)}</td>
                      <td className="text-right whitespace-nowrap">
                        {r.voucher_id && r.type !== "Opening Balance" ? (
                          <Link
                            to={`/vouchers/edit/${r.voucher_id}`}
                            state={{ from: "all-ledgers" }}
                            className="text-gray-400 hover:text-blue-700 inline-block"
                            title="Edit this voucher"
                            data-testid={`btn-edit-voucher-${r.voucher_id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Link>
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-gray-200 inline-block" />
                        )}
                      </td>
                    </tr>
                  ))}
                  {L.rows.length > 0 && (
                    <tr className="bg-gray-50 font-semibold border-t-2 border-gray-300">
                      <td colSpan={4} className="text-right">Totals</td>
                      <td className="tx-num">{formatINR(L.total_debit)}</td>
                      <td className="tx-num">{formatINR(L.total_credit)}</td>
                      <td className="tx-num">{fmtBalance(L.closing_balance)}</td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      </div>
    </>
  );
}

export default function AllLedgers() {
  return <RequireCompany><Inner /></RequireCompany>;
}
